import type { Transaction, Auth } from 'oscar-typedef'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  type ITransactionExecutor,
  type EncUVD,
  SolanaPrepareData,
  type SolanaSendExtraData,
  type AccountToken,
} from '@/types'

export class SolanaTokenTransactionExecutor implements ITransactionExecutor {
  private accountToken: AccountToken

  constructor(params: { accountToken: AccountToken }) {
    this.accountToken = params.accountToken
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<SolanaPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.accountToken.accountId,
          contract: this.accountToken.coin?.contract ?? undefined,
          to: params.to,
          tokenId: this.accountToken.tokenId?.toString(),
          amount: params.amount,
        },
      )
    if (!data.detail) {
      throw new Error('No detail data for prepare')
    }
    return new SolanaPrepareData({
      blockhash: data.detail.blockhash,
      lamportsPerSignature: data.detail.lamportsPerSignature,
      lastValidBlockHeight: data.detail.lastValidBlockHeight,
      priorityFee: data.detail.priorityFee,
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: SolanaSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    if (!this.accountToken.wallet) {
      throw new Error('No wallet for account')
    }
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.accountToken.wallet.id,
      accountId: this.accountToken.accountId,
      pin: params.encUVD,
      contract: this.accountToken.coin?.contract,
      tokenId: this.accountToken.id,
      txData: {
        txCommand: 'transfer-tkn',
        to: params.to,
        from: this.accountToken.address,
        amount: params.amount,
        blockhash: params.extraData.blockhash,
        lastValidBlockHeight: params.extraData.lastValidBlockHeight,
        lamportsPerSignature: params.extraData.lamportsPerSignature,
        priorityFee: params.extraData.priorityFee,
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    if (!this.accountToken.wallet) {
      throw new Error('No wallet for account')
    }
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.accountToken.wallet.id,
        accountId: this.accountToken.accountId,
        contract: this.accountToken.coin?.contract,
        tokenId: this.accountToken.id.toString(),
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
      },
    )
  }
}
